<template>
  <div class="relative w-full">
    <!-- Dropdown Button (Closed State) -->
    <button
      type="button"
      class="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-left flex items-center justify-between hover:bg-white/10 transition-colors"
      @click="toggleDropdown"
    >
      <div v-if="selectedOption" class="flex items-center gap-2">
        <span class="text-sm text-vault-text-primary font-medium">
          {{ selectedOption.currency.ticker.toUpperCase() }}
        </span>
        <span v-if="selectedOption.type === 'direct'" class="px-2 py-0.5 bg-green-500/20 text-green-500 text-[10px] font-semibold rounded-full">
          Direct
        </span>
        <span v-else class="px-2 py-0.5 bg-orange-500/20 text-orange-500 text-[10px] font-semibold rounded-full">
          Exchange
        </span>
        <span class="text-xs text-vault-text-secondary">
          {{ selectedOption.currency.fullName || selectedOption.currency.name }}
        </span>
      </div>
      <span v-else class="text-sm text-vault-text-secondary">
        {{ $t('currencySelector.searchPlaceholder') }}
      </span>
      <ChevronDown :size="16" class="text-vault-text-secondary flex-shrink-0" :class="{ 'rotate-180': isOpen }" />
    </button>

    <!-- Dropdown Modal (Open State) -->
    <Teleport to="body">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        @click.self="closeDropdown"
      >
        <div
          class="w-full max-w-md bg-vault-bg-primary rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
          @click.stop
        >
          <!-- Header -->
          <div class="flex items-center justify-between p-4 border-b border-white/10">
            <h3 class="text-base font-semibold text-vault-text-primary">
              {{ $t('currencySelector.title') }}
            </h3>
            <button
              type="button"
              class="p-1 hover:bg-white/10 rounded-lg transition-colors"
              @click="closeDropdown"
            >
              <X :size="20" class="text-vault-text-secondary" />
            </button>
          </div>

          <!-- Search Input -->
          <div class="p-4 border-b border-white/10">
            <div class="relative">
              <input
                ref="searchInput"
                v-model="searchQuery"
                type="text"
                :placeholder="$t('currencySelector.searchPlaceholder')"
                class="w-full px-4 py-2.5 pl-10 bg-white/5 border border-white/10 rounded-lg text-sm text-vault-text-primary placeholder-vault-text-secondary focus:outline-none focus:border-vault-blue-400 transition-all duration-200"
              />
              <Search :size="16" class="absolute left-3 top-1/2 -translate-y-1/2 text-vault-text-secondary" />
            </div>
          </div>

          <!-- Filter Chips -->
          <div v-if="!searchQuery" class="px-4 pb-3 border-b border-white/10">
            <div class="flex gap-2 overflow-x-auto scrollbar-hide">
              <button
                v-for="filter in currencyFilters"
                :key="filter.id"
                type="button"
                class="px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
                :class="activeFilter === filter.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                  : 'bg-white/5 text-vault-text-secondary hover:bg-white/10'"
                @click="setFilter(filter.id)"
              >
                {{ filter.label }}
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center py-12">
            <div class="spinner"></div>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="p-4">
            <div class="p-4 bg-red-500/5 border border-red-500/20 rounded-lg text-center">
              <p class="text-xs text-red-500/80 mb-3">{{ error }}</p>
              <button
                type="button"
                class="btn-primary !px-4 !py-2 !text-xs"
                @click="loadOptions"
              >
                {{ $t('common.retry') }}
              </button>
            </div>
          </div>

          <!-- Options List -->
          <div v-else class="max-h-[400px] overflow-y-auto custom-scrollbar">
            <!-- Unified List -->
            <div class="p-4">
              <div class="flex flex-col gap-2">
                <DepositOption
                  v-for="option in displayedOptions"
                  :key="getOptionKey(option)"
                  :option="option"
                  :is-selected="selectedOption && getOptionKey(selectedOption) === getOptionKey(option)"
                  @select="selectOption(option)"
                />
                <div v-if="displayedOptions.length === 0" class="text-center py-8 text-vault-text-secondary text-sm">
                  {{ $t('currencySelector.noResults') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Search, ChevronDown, X } from 'lucide-vue-next'
import { useCryptoDeposit } from '../../composables/useCryptoDeposit'
import DepositOption from './DepositOption.vue'

const { t: $t } = useI18n()

const props = defineProps({
  walletAddress: {
    type: String,
    required: true
  },
  walletCurrency: {
    type: Object, // CurrencyConfig
    required: true
  },
  walletNetwork: {
    type: Object, // { id, name }
    required: true
  },
  nativeCurrency: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['select'])

// Composable
const cryptoDeposit = useCryptoDeposit({
  walletAddress: props.walletAddress,
  walletCurrency: props.walletCurrency,
  walletNetwork: props.walletNetwork,
  loadRatesOnInit: false
})

// State
const isOpen = ref(false)
const searchQuery = ref('')
const searchInput = ref(null)
const selectedOption = ref(null)
const activeFilter = ref('all')

// Filter options
const currencyFilters = [
  { id: 'all', label: 'All' },
  { id: 'btc', label: 'BTC' },
  { id: 'eth', label: 'ETH' }
]

// Computed
const loading = computed(() => cryptoDeposit.loading.value)
const error = computed(() => cryptoDeposit.error.value?.message || null)

// Create native currency option
const nativeOption = computed(() => {
  if (!props.nativeCurrency) return null

  return {
    currency: {
      name: props.walletCurrency.name || props.walletCurrency.code.toUpperCase(),
      ticker: props.walletCurrency.code,
      fullName: props.walletCurrency.name || props.walletCurrency.code.toUpperCase(),
      enabled: true,
      enabledFrom: true,
      enabledTo: false,
      protocol: props.walletNetwork.id,
      blockchain: props.walletNetwork.id
    },
    network: {
      id: props.walletNetwork.id,
      name: props.walletNetwork.name,
      blockchain: props.walletNetwork.id
    },
    type: 'direct',
    displayName: props.walletCurrency.name || props.walletCurrency.code.toUpperCase(),
    displayNetwork: props.walletNetwork.name,
    isPopular: true
  }
})

// Displayed options with filtering and sorting
const displayedOptions = computed(() => {
  let options = []

  // If searching, show filtered search results
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    const allOptions = [...cryptoDeposit.depositOptions.value]

    // Add native option to search if it matches
    if (nativeOption.value) {
      const currency = nativeOption.value.currency
      const network = nativeOption.value.network
      const matches = (
        currency.ticker.toLowerCase().includes(query) ||
        currency.name?.toLowerCase().includes(query) ||
        currency.fullName?.toLowerCase().includes(query) ||
        network?.name.toLowerCase().includes(query) ||
        network?.blockchain.toLowerCase().includes(query)
      )
      if (matches) {
        allOptions.unshift(nativeOption.value)
      }
    }

    options = allOptions.filter(option => {
      const currency = option.currency
      const network = option.network

      return (
        currency.ticker.toLowerCase().includes(query) ||
        currency.name?.toLowerCase().includes(query) ||
        currency.fullName?.toLowerCase().includes(query) ||
        network?.name.toLowerCase().includes(query) ||
        network?.blockchain.toLowerCase().includes(query)
      )
    })
  } else {
    // Apply filter chips
    options = [...cryptoDeposit.depositOptions.value]

    // Add native option at the beginning if not filtering
    if (nativeOption.value && activeFilter.value === 'all') {
      options.unshift(nativeOption.value)
    }

    if (activeFilter.value === 'btc') {
      options = options.filter(opt =>
        opt.currency.ticker.toLowerCase().includes('btc')
      )
    } else if (activeFilter.value === 'eth') {
      options = options.filter(opt =>
        opt.currency.ticker.toLowerCase().includes('eth') ||
        opt.network?.blockchain.toLowerCase().includes('ethereum')
      )
    }
  }

  // Sort: direct type first, then by ticker
  return options.sort((a, b) => {
    if (a.type === 'direct' && b.type !== 'direct') return -1
    if (a.type !== 'direct' && b.type === 'direct') return 1
    return a.currency.ticker.localeCompare(b.currency.ticker)
  })
})

// Methods
function getOptionKey(option) {
  return `${option.currency.ticker}_${option.network?.id || 'default'}`
}

async function loadOptions() {
  await cryptoDeposit.loadDepositOptions()
}

function toggleDropdown() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    nextTick(() => {
      searchInput.value?.focus()
    })
  }
}

function closeDropdown() {
  isOpen.value = false
  searchQuery.value = ''
}

function selectOption(option) {
  selectedOption.value = option
  emit('select', option)
  closeDropdown()
}

function setFilter(filterId) {
  activeFilter.value = filterId
}

// Load options on mount and auto-select native currency
onMounted(async () => {
  // Auto-select native currency if provided
  if (nativeOption.value) {
    selectedOption.value = nativeOption.value
    emit('select', nativeOption.value)
  }

  // Load other options in background (for exchange options)
  loadOptions()
})

// Close dropdown on Escape key
watch(isOpen, (newValue) => {
  if (newValue) {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeDropdown()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }
})
</script>

<style scoped>
/* Loading Spinner */
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Chevron Rotation */
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Hide scrollbar for filter chips */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
